/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AttivitaMassivaRequest,
  BEResponseBoolean,
  BEResponseCollectionAzioniDocumento,
  BEResponseCollectionAzioniIstanzaSezioneDati,
  BEResponseCollectionAzioniIstanzaTipoPagamento,
  BEResponseCompletaTaskResponse,
  BEResponseDatiSpecificiRisposta,
  BEResponseEndoprocedimentiAvviabili,
  BEResponseListAttivitaWithUserTaskDTO,
  BEResponseListAzioniFirmaDocumentoResponse,
  BEResponseListAzioniGruppoAllegato,
  BEResponseListEsitoDTO,
  BEResponseListInfoLavorazioneTaskResponse,
  BEResponseListStatoAttivitaDTO,
  BEResponseListUtenteType,
  BEResponseObject,
  BEResponseRicercaAttivitaResponseDTO,
  BEResponseString,
  BEResponseUtenteAssegnatarioTaskInfo,
  CampoEstraibileCSV,
  CompletaDTO,
  ComunicazioneAnteprimaDTO,
  ComunicazioneCreateRequest,
  ComunicazioneDTO,
  ComunicazioneManualeCreateRequest,
  ComunicazioneManualeRequest,
  ComunicazioneUpdateRequest,
  CreazioneSezioneDatiDto,
  ElencoFileDocumento,
  GetBackAttivitaAzioniFirmaDocumentiParams,
  GetBackAttivitaCsvParams,
  GetBackAttivitaIdComunicazioniParams,
  GetBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  GetBackAttivitaIdTipiComunicazioneParams,
  GetBackAttivitaParams,
  GetBackAttivitaXlsxParams,
  PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody,
  PostBackAttivitaIdDocumentiIdContenutoFirmatoBody,
  PostBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody,
  RiassegnaTaskMassivoRequest,
  RiassegnaTaskRequest,
  RicercaUtentiCandidatiMassivoRequest,
  RilascioMassivoRequest,
  TipoComunicazioneDTO,
  WsRispostaPaginataListComunicazioneDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Rilascia un'attività dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di rilascio
 */
export const putBackAttivitaIdRilascia = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseObject>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/rilascia`,
      method: 'put',
    },
    options,
  );
};

export const getPutBackAttivitaIdRilasciaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdRilascia>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdRilascia>>,
  TError,
  { idTaskInstance: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdRilascia>>,
    { idTaskInstance: string }
  > = props => {
    const { idTaskInstance } = props ?? {};

    return putBackAttivitaIdRilascia(idTaskInstance, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdRilasciaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdRilascia>>
>;

export type PutBackAttivitaIdRilasciaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di rilascio
 */
export const usePutBackAttivitaIdRilascia = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdRilascia>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaIdRilasciaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Assegna un'attività a un altro utente dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di assegnazione a un altro utente
 */
export const putBackAttivitaIdRiassegna = (
  idTaskInstance: string,
  riassegnaTaskRequest: RiassegnaTaskRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseBoolean>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/riassegna`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: riassegnaTaskRequest,
    },
    options,
  );
};

export const getPutBackAttivitaIdRiassegnaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdRiassegna>>,
    TError,
    { idTaskInstance: string; data: RiassegnaTaskRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdRiassegna>>,
  TError,
  { idTaskInstance: string; data: RiassegnaTaskRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdRiassegna>>,
    { idTaskInstance: string; data: RiassegnaTaskRequest }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return putBackAttivitaIdRiassegna(idTaskInstance, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdRiassegnaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdRiassegna>>
>;
export type PutBackAttivitaIdRiassegnaMutationBody = RiassegnaTaskRequest;
export type PutBackAttivitaIdRiassegnaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di assegnazione a un altro utente
 */
export const usePutBackAttivitaIdRiassegna = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdRiassegna>>,
    TError,
    { idTaskInstance: string; data: RiassegnaTaskRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaIdRiassegnaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna una lista di dichiarazione dato un idTask passato
 * @summary Ricerca istanze-sezioni-dati
 */
export const getBackAttivitaIdIstanzeSezioniDati = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseCollectionAzioniIstanzaSezioneDati>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-sezioni-dati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdIstanzeSezioniDatiQueryKey = (
  idTaskInstance: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-sezioni-dati`,
  ] as const;

export const getGetBackAttivitaIdIstanzeSezioniDatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdIstanzeSezioniDatiQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>
  > = ({ signal }) =>
    getBackAttivitaIdIstanzeSezioniDati(idTaskInstance, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdIstanzeSezioniDatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>
>;
export type GetBackAttivitaIdIstanzeSezioniDatiQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca istanze-sezioni-dati
 */
export const useGetBackAttivitaIdIstanzeSezioniDati = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdIstanzeSezioniDati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdIstanzeSezioniDatiQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna tutte le istanze-sezioni-dati
 * @summary Update massivo
 */
export const putBackAttivitaIdIstanzeSezioniDati = (
  idTaskInstance: string,
  creazioneSezioneDatiDto: CreazioneSezioneDatiDto[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseDatiSpecificiRisposta>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-sezioni-dati`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creazioneSezioneDatiDto,
    },
    options,
  );
};

export const getPutBackAttivitaIdIstanzeSezioniDatiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdIstanzeSezioniDati>>,
    TError,
    { idTaskInstance: string; data: CreazioneSezioneDatiDto[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdIstanzeSezioniDati>>,
  TError,
  { idTaskInstance: string; data: CreazioneSezioneDatiDto[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdIstanzeSezioniDati>>,
    { idTaskInstance: string; data: CreazioneSezioneDatiDto[] }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return putBackAttivitaIdIstanzeSezioniDati(
      idTaskInstance,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdIstanzeSezioniDatiMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdIstanzeSezioniDati>>
>;
export type PutBackAttivitaIdIstanzeSezioniDatiMutationBody =
  CreazioneSezioneDatiDto[];
export type PutBackAttivitaIdIstanzeSezioniDatiMutationError =
  ErrorType<unknown>;

/**
 * @summary Update massivo
 */
export const usePutBackAttivitaIdIstanzeSezioniDati = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdIstanzeSezioniDati>>,
    TError,
    { idTaskInstance: string; data: CreazioneSezioneDatiDto[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaIdIstanzeSezioniDatiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Forza il rilascio di una attività dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di rilascio
 */
export const putBackAttivitaIdForzaRilascio = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseUtenteAssegnatarioTaskInfo>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/forza-rilascio`,
      method: 'put',
    },
    options,
  );
};

export const getPutBackAttivitaIdForzaRilascioMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdForzaRilascio>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdForzaRilascio>>,
  TError,
  { idTaskInstance: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdForzaRilascio>>,
    { idTaskInstance: string }
  > = props => {
    const { idTaskInstance } = props ?? {};

    return putBackAttivitaIdForzaRilascio(idTaskInstance, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdForzaRilascioMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdForzaRilascio>>
>;

export type PutBackAttivitaIdForzaRilascioMutationError = ErrorType<unknown>;

/**
 * @summary Azione di rilascio
 */
export const usePutBackAttivitaIdForzaRilascio = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdForzaRilascio>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaIdForzaRilascioMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca puntuale Comunicazione
 * @summary Ricerca puntuale Comunicazione
 */
export const getBackAttivitaIdComunicazioniId = (
  idTaskInstance: string,
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ComunicazioneDTO>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${idComunicazione}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdComunicazioniIdQueryKey = (
  idTaskInstance: string,
  idComunicazione: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${idComunicazione}`,
  ] as const;

export const getGetBackAttivitaIdComunicazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdComunicazioniIdQueryKey(
      idTaskInstance,
      idComunicazione,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>
  > = ({ signal }) =>
    getBackAttivitaIdComunicazioniId(
      idTaskInstance,
      idComunicazione,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idTaskInstance && idComunicazione),
    ...queryOptions,
  };
};

export type GetBackAttivitaIdComunicazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>
>;
export type GetBackAttivitaIdComunicazioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca puntuale Comunicazione
 */
export const useGetBackAttivitaIdComunicazioniId = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idComunicazione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdComunicazioniIdQueryOptions(
    idTaskInstance,
    idComunicazione,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Update di una comunicazione
 * @summary Update di una comunicazione
 */
export const putBackAttivitaIdComunicazioniId = (
  idTaskInstance: string,
  idComunicazione: string,
  comunicazioneUpdateRequest: ComunicazioneUpdateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${idComunicazione}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneUpdateRequest,
    },
    options,
  );
};

export const getPutBackAttivitaIdComunicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniId>>,
    TError,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneUpdateRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniId>>,
  TError,
  {
    idTaskInstance: string;
    idComunicazione: string;
    data: ComunicazioneUpdateRequest;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniId>>,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneUpdateRequest;
    }
  > = props => {
    const { idTaskInstance, idComunicazione, data } = props ?? {};

    return putBackAttivitaIdComunicazioniId(
      idTaskInstance,
      idComunicazione,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdComunicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniId>>
>;
export type PutBackAttivitaIdComunicazioniIdMutationBody =
  ComunicazioneUpdateRequest;
export type PutBackAttivitaIdComunicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Update di una comunicazione
 */
export const usePutBackAttivitaIdComunicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniId>>,
    TError,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneUpdateRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaIdComunicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella una Comunicazione
 * @summary Cancella una Comunicazione
 */
export const deleteBackAttivitaIdComunicazioniId = (
  idTaskInstance: string,
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${idComunicazione}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackAttivitaIdComunicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniId>>,
    TError,
    { idTaskInstance: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniId>>,
  TError,
  { idTaskInstance: string; idComunicazione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniId>>,
    { idTaskInstance: string; idComunicazione: string }
  > = props => {
    const { idTaskInstance, idComunicazione } = props ?? {};

    return deleteBackAttivitaIdComunicazioniId(
      idTaskInstance,
      idComunicazione,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackAttivitaIdComunicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniId>>
>;

export type DeleteBackAttivitaIdComunicazioniIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Cancella una Comunicazione
 */
export const useDeleteBackAttivitaIdComunicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniId>>,
    TError,
    { idTaskInstance: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackAttivitaIdComunicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Update di una comunicazione manuale
 * @summary Update di una comunicazione manuale
 */
export const putBackAttivitaIdComunicazioniManualiId = (
  idTaskInstance: string,
  idComunicazione: string,
  comunicazioneManualeRequest: ComunicazioneManualeRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni-manuali/${idComunicazione}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneManualeRequest,
    },
    options,
  );
};

export const getPutBackAttivitaIdComunicazioniManualiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniManualiId>>,
    TError,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneManualeRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniManualiId>>,
  TError,
  {
    idTaskInstance: string;
    idComunicazione: string;
    data: ComunicazioneManualeRequest;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniManualiId>>,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneManualeRequest;
    }
  > = props => {
    const { idTaskInstance, idComunicazione, data } = props ?? {};

    return putBackAttivitaIdComunicazioniManualiId(
      idTaskInstance,
      idComunicazione,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdComunicazioniManualiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniManualiId>>
>;
export type PutBackAttivitaIdComunicazioniManualiIdMutationBody =
  ComunicazioneManualeRequest;
export type PutBackAttivitaIdComunicazioniManualiIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Update di una comunicazione manuale
 */
export const usePutBackAttivitaIdComunicazioniManualiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdComunicazioniManualiId>>,
    TError,
    {
      idTaskInstance: string;
      idComunicazione: string;
      data: ComunicazioneManualeRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaIdComunicazioniManualiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Completa un'attività dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di completamento
 */
export const putBackAttivitaIdCompleta = (
  idTaskInstance: string,
  completaDTO: CompletaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseCompletaTaskResponse>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/completa`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: completaDTO,
    },
    options,
  );
};

export const getPutBackAttivitaIdCompletaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdCompleta>>,
    TError,
    { idTaskInstance: string; data: CompletaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdCompleta>>,
  TError,
  { idTaskInstance: string; data: CompletaDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdCompleta>>,
    { idTaskInstance: string; data: CompletaDTO }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return putBackAttivitaIdCompleta(idTaskInstance, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdCompletaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdCompleta>>
>;
export type PutBackAttivitaIdCompletaMutationBody = CompletaDTO;
export type PutBackAttivitaIdCompletaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di completamento
 */
export const usePutBackAttivitaIdCompleta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdCompleta>>,
    TError,
    { idTaskInstance: string; data: CompletaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaIdCompletaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Assegna un'attività dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di assegnazione
 */
export const putBackAttivitaIdAssegna = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseObject>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/assegna`,
      method: 'put',
    },
    options,
  );
};

export const getPutBackAttivitaIdAssegnaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdAssegna>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdAssegna>>,
  TError,
  { idTaskInstance: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdAssegna>>,
    { idTaskInstance: string }
  > = props => {
    const { idTaskInstance } = props ?? {};

    return putBackAttivitaIdAssegna(idTaskInstance, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdAssegnaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdAssegna>>
>;

export type PutBackAttivitaIdAssegnaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di assegnazione
 */
export const usePutBackAttivitaIdAssegna = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdAssegna>>,
    TError,
    { idTaskInstance: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaIdAssegnaMutationOptions(options);

  return useMutation(mutationOptions);
};
export const putBackAttivitaUtentiCandidati = (
  ricercaUtentiCandidatiMassivoRequest: RicercaUtentiCandidatiMassivoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseListUtenteType>(
    {
      url: `/framework-bo-procedimenti/attivita/utentiCandidati`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: ricercaUtentiCandidatiMassivoRequest,
    },
    options,
  );
};

export const getPutBackAttivitaUtentiCandidatiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaUtentiCandidati>>,
    TError,
    { data: RicercaUtentiCandidatiMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaUtentiCandidati>>,
  TError,
  { data: RicercaUtentiCandidatiMassivoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaUtentiCandidati>>,
    { data: RicercaUtentiCandidatiMassivoRequest }
  > = props => {
    const { data } = props ?? {};

    return putBackAttivitaUtentiCandidati(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaUtentiCandidatiMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaUtentiCandidati>>
>;
export type PutBackAttivitaUtentiCandidatiMutationBody =
  RicercaUtentiCandidatiMassivoRequest;
export type PutBackAttivitaUtentiCandidatiMutationError = ErrorType<unknown>;

export const usePutBackAttivitaUtentiCandidati = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaUtentiCandidati>>,
    TError,
    { data: RicercaUtentiCandidatiMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaUtentiCandidatiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rilascia un'attività dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di rilascio Massivo
 */
export const putBackAttivitaRilascia = (
  rilascioMassivoRequest: RilascioMassivoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString[]>(
    {
      url: `/framework-bo-procedimenti/attivita/rilascia`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: rilascioMassivoRequest,
    },
    options,
  );
};

export const getPutBackAttivitaRilasciaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaRilascia>>,
    TError,
    { data: RilascioMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaRilascia>>,
  TError,
  { data: RilascioMassivoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaRilascia>>,
    { data: RilascioMassivoRequest }
  > = props => {
    const { data } = props ?? {};

    return putBackAttivitaRilascia(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaRilasciaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaRilascia>>
>;
export type PutBackAttivitaRilasciaMutationBody = RilascioMassivoRequest;
export type PutBackAttivitaRilasciaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di rilascio Massivo
 */
export const usePutBackAttivitaRilascia = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaRilascia>>,
    TError,
    { data: RilascioMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaRilasciaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Assegna più attività a un altro utente dato il suo id e ritorna l'esito dell'operazione
 * @summary Azione di assegnazione massiva a un altro utente
 */
export const putBackAttivitaRiassegna = (
  riassegnaTaskMassivoRequest: RiassegnaTaskMassivoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString[]>(
    {
      url: `/framework-bo-procedimenti/attivita/riassegna`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: riassegnaTaskMassivoRequest,
    },
    options,
  );
};

export const getPutBackAttivitaRiassegnaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaRiassegna>>,
    TError,
    { data: RiassegnaTaskMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaRiassegna>>,
  TError,
  { data: RiassegnaTaskMassivoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaRiassegna>>,
    { data: RiassegnaTaskMassivoRequest }
  > = props => {
    const { data } = props ?? {};

    return putBackAttivitaRiassegna(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaRiassegnaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaRiassegna>>
>;
export type PutBackAttivitaRiassegnaMutationBody = RiassegnaTaskMassivoRequest;
export type PutBackAttivitaRiassegnaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di assegnazione massiva a un altro utente
 */
export const usePutBackAttivitaRiassegna = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaRiassegna>>,
    TError,
    { data: RiassegnaTaskMassivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaRiassegnaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Assegna più attività all'utente collegato e restituisce l'esito dell'operazione
 * @summary Azione di assegnazione massiva
 */
export const putBackAttivitaAssegna = (
  attivitaMassivaRequest: AttivitaMassivaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString[]>(
    {
      url: `/framework-bo-procedimenti/attivita/assegna`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: attivitaMassivaRequest,
    },
    options,
  );
};

export const getPutBackAttivitaAssegnaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaAssegna>>,
    TError,
    { data: AttivitaMassivaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaAssegna>>,
  TError,
  { data: AttivitaMassivaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaAssegna>>,
    { data: AttivitaMassivaRequest }
  > = props => {
    const { data } = props ?? {};

    return putBackAttivitaAssegna(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaAssegnaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaAssegna>>
>;
export type PutBackAttivitaAssegnaMutationBody = AttivitaMassivaRequest;
export type PutBackAttivitaAssegnaMutationError = ErrorType<unknown>;

/**
 * @summary Azione di assegnazione massiva
 */
export const usePutBackAttivitaAssegna = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaAssegna>>,
    TError,
    { data: AttivitaMassivaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackAttivitaAssegnaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Schedula la comunicazione per l'invio e la protocollazione se il tipoComunicazione associato lo prevede
 * @summary Schedula la comunicazione per l'invio e la protocollazione se il tipoComunicazione associato lo prevede
 */
export const postBackAttivitaIdInviaComunicazioneId = (
  idTaskInstance: string,
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/inviaComunicazione/${idComunicazione}`,
      method: 'post',
    },
    options,
  );
};

export const getPostBackAttivitaIdInviaComunicazioneIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdInviaComunicazioneId>>,
    TError,
    { idTaskInstance: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdInviaComunicazioneId>>,
  TError,
  { idTaskInstance: string; idComunicazione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdInviaComunicazioneId>>,
    { idTaskInstance: string; idComunicazione: string }
  > = props => {
    const { idTaskInstance, idComunicazione } = props ?? {};

    return postBackAttivitaIdInviaComunicazioneId(
      idTaskInstance,
      idComunicazione,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdInviaComunicazioneIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackAttivitaIdInviaComunicazioneId>>
>;

export type PostBackAttivitaIdInviaComunicazioneIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Schedula la comunicazione per l'invio e la protocollazione se il tipoComunicazione associato lo prevede
 */
export const usePostBackAttivitaIdInviaComunicazioneId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdInviaComunicazioneId>>,
    TError,
    { idTaskInstance: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdInviaComunicazioneIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postBackAttivitaIdDocumentoIdSezioniDocumentoId = (
  idTaskInstance: string,
  idDocumento: string,
  idTipoSezione: string,
  postBackAttivitaIdDocumentoIdSezioniDocumentoIdBody: PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postBackAttivitaIdDocumentoIdSezioniDocumentoIdBody.file,
  );

  return apiClientRequest<BEResponseObject>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/documento/${idDocumento}/sezioniDocumento/${idTipoSezione}`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostBackAttivitaIdDocumentoIdSezioniDocumentoIdMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postBackAttivitaIdDocumentoIdSezioniDocumentoId>
      >,
      TError,
      {
        idTaskInstance: string;
        idDocumento: string;
        idTipoSezione: string;
        data: PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentoIdSezioniDocumentoId>>,
    TError,
    {
      idTaskInstance: string;
      idDocumento: string;
      idTipoSezione: string;
      data: PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postBackAttivitaIdDocumentoIdSezioniDocumentoId>
      >,
      {
        idTaskInstance: string;
        idDocumento: string;
        idTipoSezione: string;
        data: PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody;
      }
    > = props => {
      const { idTaskInstance, idDocumento, idTipoSezione, data } = props ?? {};

      return postBackAttivitaIdDocumentoIdSezioniDocumentoId(
        idTaskInstance,
        idDocumento,
        idTipoSezione,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostBackAttivitaIdDocumentoIdSezioniDocumentoIdMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentoIdSezioniDocumentoId>>
  >;
export type PostBackAttivitaIdDocumentoIdSezioniDocumentoIdMutationBody =
  PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody;
export type PostBackAttivitaIdDocumentoIdSezioniDocumentoIdMutationError =
  ErrorType<unknown>;

export const usePostBackAttivitaIdDocumentoIdSezioniDocumentoId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentoIdSezioniDocumentoId>>,
    TError,
    {
      idTaskInstance: string;
      idDocumento: string;
      idTipoSezione: string;
      data: PostBackAttivitaIdDocumentoIdSezioniDocumentoIdBody;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdDocumentoIdSezioniDocumentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
export const getBackAttivitaIdDocumentiIdContenutoFirmato = (
  idTaskInstance: string,
  idDocumento: string,
  params: GetBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/documenti/${idDocumento}/contenutoFirmato`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdDocumentiIdContenutoFirmatoQueryKey = (
  idTaskInstance: string,
  idDocumento: string,
  params: GetBackAttivitaIdDocumentiIdContenutoFirmatoParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/documenti/${idDocumento}/contenutoFirmato`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaIdDocumentiIdContenutoFirmatoQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idDocumento: string,
  params: GetBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdDocumentiIdContenutoFirmatoQueryKey(
      idTaskInstance,
      idDocumento,
      params,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>>
  > = ({ signal }) =>
    getBackAttivitaIdDocumentiIdContenutoFirmato(
      idTaskInstance,
      idDocumento,
      params,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idTaskInstance && idDocumento),
    ...queryOptions,
  };
};

export type GetBackAttivitaIdDocumentiIdContenutoFirmatoQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>>
  >;
export type GetBackAttivitaIdDocumentiIdContenutoFirmatoQueryError =
  ErrorType<unknown>;

export const useGetBackAttivitaIdDocumentiIdContenutoFirmato = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idDocumento: string,
  params: GetBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdDocumentiIdContenutoFirmato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackAttivitaIdDocumentiIdContenutoFirmatoQueryOptions(
      idTaskInstance,
      idDocumento,
      params,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const postBackAttivitaIdDocumentiIdContenutoFirmato = (
  idTaskInstance: string,
  idDocumento: string,
  postBackAttivitaIdDocumentiIdContenutoFirmatoBody: PostBackAttivitaIdDocumentiIdContenutoFirmatoBody,
  params: PostBackAttivitaIdDocumentiIdContenutoFirmatoParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postBackAttivitaIdDocumentiIdContenutoFirmatoBody.file,
  );

  return apiClientRequest<BEResponseString>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/documenti/${idDocumento}/contenutoFirmato`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostBackAttivitaIdDocumentiIdContenutoFirmatoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentiIdContenutoFirmato>>,
    TError,
    {
      idTaskInstance: string;
      idDocumento: string;
      data: PostBackAttivitaIdDocumentiIdContenutoFirmatoBody;
      params: PostBackAttivitaIdDocumentiIdContenutoFirmatoParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdDocumentiIdContenutoFirmato>>,
  TError,
  {
    idTaskInstance: string;
    idDocumento: string;
    data: PostBackAttivitaIdDocumentiIdContenutoFirmatoBody;
    params: PostBackAttivitaIdDocumentiIdContenutoFirmatoParams;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentiIdContenutoFirmato>>,
    {
      idTaskInstance: string;
      idDocumento: string;
      data: PostBackAttivitaIdDocumentiIdContenutoFirmatoBody;
      params: PostBackAttivitaIdDocumentiIdContenutoFirmatoParams;
    }
  > = props => {
    const { idTaskInstance, idDocumento, data, params } = props ?? {};

    return postBackAttivitaIdDocumentiIdContenutoFirmato(
      idTaskInstance,
      idDocumento,
      data,
      params,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdDocumentiIdContenutoFirmatoMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentiIdContenutoFirmato>>
  >;
export type PostBackAttivitaIdDocumentiIdContenutoFirmatoMutationBody =
  PostBackAttivitaIdDocumentiIdContenutoFirmatoBody;
export type PostBackAttivitaIdDocumentiIdContenutoFirmatoMutationError =
  ErrorType<unknown>;

export const usePostBackAttivitaIdDocumentiIdContenutoFirmato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdDocumentiIdContenutoFirmato>>,
    TError,
    {
      idTaskInstance: string;
      idDocumento: string;
      data: PostBackAttivitaIdDocumentiIdContenutoFirmatoBody;
      params: PostBackAttivitaIdDocumentiIdContenutoFirmatoParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdDocumentiIdContenutoFirmatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca paginata filtrate per idProcedimento delle Comunicazioni. E' ordinabile per idTipoComunicazione
 * @deprecated
 * @summary Ricerca paginata filtrate per idProcedimento delle Comunicazioni
 */
export const getBackAttivitaIdComunicazioni = (
  idTaskInstance: string,
  params?: GetBackAttivitaIdComunicazioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListComunicazioneDTO>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdComunicazioniQueryKey = (
  idTaskInstance: string,
  params?: GetBackAttivitaIdComunicazioniParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaIdComunicazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  params?: GetBackAttivitaIdComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdComunicazioniQueryKey(idTaskInstance, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>
  > = ({ signal }) =>
    getBackAttivitaIdComunicazioni(
      idTaskInstance,
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdComunicazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>
>;
export type GetBackAttivitaIdComunicazioniQueryError = ErrorType<unknown>;

/**
 * @deprecated
 * @summary Ricerca paginata filtrate per idProcedimento delle Comunicazioni
 */
export const useGetBackAttivitaIdComunicazioni = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  params?: GetBackAttivitaIdComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdComunicazioniQueryOptions(
    idTaskInstance,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea una Comunicazione
 * @summary Crea una Comunicazione
 */
export const postBackAttivitaIdComunicazioni = (
  idTaskInstance: string,
  comunicazioneCreateRequest: ComunicazioneCreateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneCreateRequest,
    },
    options,
  );
};

export const getPostBackAttivitaIdComunicazioniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioni>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdComunicazioni>>,
  TError,
  { idTaskInstance: string; data: ComunicazioneCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioni>>,
    { idTaskInstance: string; data: ComunicazioneCreateRequest }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return postBackAttivitaIdComunicazioni(
      idTaskInstance,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdComunicazioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackAttivitaIdComunicazioni>>
>;
export type PostBackAttivitaIdComunicazioniMutationBody =
  ComunicazioneCreateRequest;
export type PostBackAttivitaIdComunicazioniMutationError = ErrorType<unknown>;

/**
 * @summary Crea una Comunicazione
 */
export const usePostBackAttivitaIdComunicazioni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioni>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdComunicazioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiunge un allegato ad una comunicazione
 * @summary Aggiunge un allegato ad una comunicazione
 */
export const postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento = (
  idTaskInstance: string,
  id: string,
  idTipoAllegato: string,
  elencoFileDocumento: ElencoFileDocumento,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${id}/tipi-allegato/${idTipoAllegato}/file-procedimento`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: elencoFileDocumento,
    },
    options,
  );
};

export const getPostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimentoMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento
        >
      >,
      TError,
      {
        idTaskInstance: string;
        id: string;
        idTipoAllegato: string;
        data: ElencoFileDocumento;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento
      >
    >,
    TError,
    {
      idTaskInstance: string;
      id: string;
      idTipoAllegato: string;
      data: ElencoFileDocumento;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento
        >
      >,
      {
        idTaskInstance: string;
        id: string;
        idTipoAllegato: string;
        data: ElencoFileDocumento;
      }
    > = props => {
      const { idTaskInstance, id, idTipoAllegato, data } = props ?? {};

      return postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento(
        idTaskInstance,
        id,
        idTipoAllegato,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimentoMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento
      >
    >
  >;
export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimentoMutationBody =
  ElencoFileDocumento;
export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimentoMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiunge un allegato ad una comunicazione
 */
export const usePostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimento
        >
      >,
      TError,
      {
        idTaskInstance: string;
        id: string;
        idTipoAllegato: string;
        data: ElencoFileDocumento;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPostBackAttivitaIdComunicazioniIdTipiAllegatoIdFileProcedimentoMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * Crea un allegato e ne fa l'upload nel sistema di storage
 * @summary Crea un allegato e ne fa l'upload nel sistema di storage
 */
export const postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati = (
  idTaskInstance: string,
  id: string,
  idTipoAllegato: string,
  postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody: PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody.file,
  );

  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/${id}/tipi-allegato/${idTipoAllegato}/allegati`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati
        >
      >,
      TError,
      {
        idTaskInstance: string;
        id: string;
        idTipoAllegato: string;
        data: PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati>
    >,
    TError,
    {
      idTaskInstance: string;
      id: string;
      idTipoAllegato: string;
      data: PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati
        >
      >,
      {
        idTaskInstance: string;
        id: string;
        idTipoAllegato: string;
        data: PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody;
      }
    > = props => {
      const { idTaskInstance, id, idTipoAllegato, data } = props ?? {};

      return postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati(
        idTaskInstance,
        id,
        idTipoAllegato,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati>
    >
  >;
export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiMutationBody =
  PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody;
export type PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiMutationError =
  ErrorType<unknown>;

/**
 * @summary Crea un allegato e ne fa l'upload nel sistema di storage
 */
export const usePostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof postBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegati>
    >,
    TError,
    {
      idTaskInstance: string;
      id: string;
      idTipoAllegato: string;
      data: PostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiBody;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdComunicazioniIdTipiAllegatoIdAllegatiMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * Crea una anteprima della comunicazione con popolati oggetto e messaggio
 * @summary Crea una anteprima della comunicazione con popolati oggetto e messaggio
 */
export const postBackAttivitaIdComunicazioniAnteprima = (
  idTaskInstance: string,
  comunicazioneCreateRequest: ComunicazioneCreateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ComunicazioneAnteprimaDTO>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/anteprima`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneCreateRequest,
    },
    options,
  );
};

export const getPostBackAttivitaIdComunicazioniAnteprimaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniAnteprima>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniAnteprima>>,
  TError,
  { idTaskInstance: string; data: ComunicazioneCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniAnteprima>>,
    { idTaskInstance: string; data: ComunicazioneCreateRequest }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return postBackAttivitaIdComunicazioniAnteprima(
      idTaskInstance,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdComunicazioniAnteprimaMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniAnteprima>>
  >;
export type PostBackAttivitaIdComunicazioniAnteprimaMutationBody =
  ComunicazioneCreateRequest;
export type PostBackAttivitaIdComunicazioniAnteprimaMutationError =
  ErrorType<unknown>;

/**
 * @summary Crea una anteprima della comunicazione con popolati oggetto e messaggio
 */
export const usePostBackAttivitaIdComunicazioniAnteprima = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniAnteprima>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdComunicazioniAnteprimaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una Comunicazione manuale
 * @summary Crea una Comunicazione manuale
 */
export const postBackAttivitaIdComunicazioniManuali = (
  idTaskInstance: string,
  comunicazioneManualeCreateRequest: ComunicazioneManualeCreateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni-manuali`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneManualeCreateRequest,
    },
    options,
  );
};

export const getPostBackAttivitaIdComunicazioniManualiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniManuali>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneManualeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniManuali>>,
  TError,
  { idTaskInstance: string; data: ComunicazioneManualeCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniManuali>>,
    { idTaskInstance: string; data: ComunicazioneManualeCreateRequest }
  > = props => {
    const { idTaskInstance, data } = props ?? {};

    return postBackAttivitaIdComunicazioniManuali(
      idTaskInstance,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdComunicazioniManualiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniManuali>>
>;
export type PostBackAttivitaIdComunicazioniManualiMutationBody =
  ComunicazioneManualeCreateRequest;
export type PostBackAttivitaIdComunicazioniManualiMutationError =
  ErrorType<unknown>;

/**
 * @summary Crea una Comunicazione manuale
 */
export const usePostBackAttivitaIdComunicazioniManuali = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdComunicazioniManuali>>,
    TError,
    { idTaskInstance: string; data: ComunicazioneManualeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdComunicazioniManualiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna una lista paginata di attività secondo i criteri di ricerca
 * @summary Ricerca attività
 */
export const getBackAttivita = (
  params?: GetBackAttivitaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseRicercaAttivitaResponseDTO>(
    {
      url: `/framework-bo-procedimenti/attivita`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaQueryKey = (params?: GetBackAttivitaParams) =>
  [`/framework-bo-procedimenti/attivita`, ...(params ? [params] : [])] as const;

export const getGetBackAttivitaQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivita>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivita>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivita>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBackAttivitaQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBackAttivita>>> = ({
    signal,
  }) => getBackAttivita(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivita>>
>;
export type GetBackAttivitaQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca attività
 */
export const useGetBackAttivita = <
  TData = Awaited<ReturnType<typeof getBackAttivita>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivita>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna una lista di attività attivabili dall'id istanza procedimento usato
 * @summary Ricerca attività attivabili
 */
export const getBackAttivitaIdAttivabili = (
  processInstanceId: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListAttivitaWithUserTaskDTO>(
    {
      url: `/framework-bo-procedimenti/attivita/${processInstanceId}/attivabili`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdAttivabiliQueryKey = (
  processInstanceId: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${processInstanceId}/attivabili`,
  ] as const;

export const getGetBackAttivitaIdAttivabiliQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>,
  TError = ErrorType<unknown>,
>(
  processInstanceId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdAttivabiliQueryKey(processInstanceId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>
  > = ({ signal }) =>
    getBackAttivitaIdAttivabili(processInstanceId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!processInstanceId, ...queryOptions };
};

export type GetBackAttivitaIdAttivabiliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>
>;
export type GetBackAttivitaIdAttivabiliQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca attività attivabili
 */
export const useGetBackAttivitaIdAttivabili = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>,
  TError = ErrorType<unknown>,
>(
  processInstanceId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAttivabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdAttivabiliQueryOptions(
    processInstanceId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaIdUtentiCandidati = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListUtenteType>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/utentiCandidati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdUtentiCandidatiQueryKey = (
  idTaskInstance: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/utentiCandidati`,
  ] as const;

export const getGetBackAttivitaIdUtentiCandidatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdUtentiCandidatiQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>
  > = ({ signal }) =>
    getBackAttivitaIdUtentiCandidati(idTaskInstance, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdUtentiCandidatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>
>;
export type GetBackAttivitaIdUtentiCandidatiQueryError = ErrorType<unknown>;

export const useGetBackAttivitaIdUtentiCandidati = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdUtentiCandidati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdUtentiCandidatiQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ricerca tipi comunicazione associati a uno user task. Ordinabile per codice tipo comunicazione se a true il campo ordinaPerCodice in requestParam
 * @summary Ricerca tipi comunicazione associati a uno user task
 */
export const getBackAttivitaIdTipiComunicazione = (
  idTaskInstance: string,
  params?: GetBackAttivitaIdTipiComunicazioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/tipi-comunicazione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdTipiComunicazioneQueryKey = (
  idTaskInstance: string,
  params?: GetBackAttivitaIdTipiComunicazioneParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/tipi-comunicazione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaIdTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  params?: GetBackAttivitaIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdTipiComunicazioneQueryKey(idTaskInstance, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>
  > = ({ signal }) =>
    getBackAttivitaIdTipiComunicazione(
      idTaskInstance,
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>
>;
export type GetBackAttivitaIdTipiComunicazioneQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca tipi comunicazione associati a uno user task
 */
export const useGetBackAttivitaIdTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  params?: GetBackAttivitaIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdTipiComunicazioneQueryOptions(
    idTaskInstance,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna una lista di istanze-tipi-pagamento dato un idTask passato
 * @summary Ricerca istanze-tipi-pagamento
 */
export const getBackAttivitaIdIstanzeTipiPagamento = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseCollectionAzioniIstanzaTipoPagamento>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-tipi-pagamento`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdIstanzeTipiPagamentoQueryKey = (
  idTaskInstance: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-tipi-pagamento`,
  ] as const;

export const getGetBackAttivitaIdIstanzeTipiPagamentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdIstanzeTipiPagamentoQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>
  > = ({ signal }) =>
    getBackAttivitaIdIstanzeTipiPagamento(
      idTaskInstance,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdIstanzeTipiPagamentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>
>;
export type GetBackAttivitaIdIstanzeTipiPagamentoQueryError =
  ErrorType<unknown>;

/**
 * @summary Ricerca istanze-tipi-pagamento
 */
export const useGetBackAttivitaIdIstanzeTipiPagamento = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdIstanzeTipiPagamentoQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Esegue il download della versione PDF della sezione dati di una istanza tipo pagamento senza chiudere/completare l’attività
 * @summary download pdf del preventivo
 */
export const getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo = (
  idTaskInstance: string,
  idIstanzaTipoPagamento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-tipi-pagamento/${idIstanzaTipoPagamento}/scarica-preventivo`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryKey =
  (idTaskInstance: string, idIstanzaTipoPagamento: string) =>
    [
      `/framework-bo-procedimenti/attivita/${idTaskInstance}/istanze-tipi-pagamento/${idIstanzaTipoPagamento}/scarica-preventivo`,
    ] as const;

export const getGetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
      >
    >,
    TError = ErrorType<unknown>,
  >(
    idTaskInstance: string,
    idIstanzaTipoPagamento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryKey(
        idTaskInstance,
        idIstanzaTipoPagamento,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
        >
      >
    > = ({ signal }) =>
      getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo(
        idTaskInstance,
        idIstanzaTipoPagamento,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(idTaskInstance && idIstanzaTipoPagamento),
      ...queryOptions,
    };
  };

export type GetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
      >
    >
  >;
export type GetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryError =
  ErrorType<unknown>;

/**
 * @summary download pdf del preventivo
 */
export const useGetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idIstanzaTipoPagamento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivo
        >
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackAttivitaIdIstanzeTipiPagamentoIdScaricaPreventivoQueryOptions(
      idTaskInstance,
      idIstanzaTipoPagamento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaIdInfoLavorazioneTask = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListInfoLavorazioneTaskResponse>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/infoLavorazioneTask`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdInfoLavorazioneTaskQueryKey = (
  idTaskInstance: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/infoLavorazioneTask`,
  ] as const;

export const getGetBackAttivitaIdInfoLavorazioneTaskQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdInfoLavorazioneTaskQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>
  > = ({ signal }) =>
    getBackAttivitaIdInfoLavorazioneTask(
      idTaskInstance,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdInfoLavorazioneTaskQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>
>;
export type GetBackAttivitaIdInfoLavorazioneTaskQueryError = ErrorType<unknown>;

export const useGetBackAttivitaIdInfoLavorazioneTask = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdInfoLavorazioneTask>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdInfoLavorazioneTaskQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna una lista di esisti di tipo EsitoDTO di un'attività
 * @summary Esiti
 */
export const getBackAttivitaIdEsiti = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListEsitoDTO>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/esiti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdEsitiQueryKey = (idTaskInstance: string) =>
  [`/framework-bo-procedimenti/attivita/${idTaskInstance}/esiti`] as const;

export const getGetBackAttivitaIdEsitiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackAttivitaIdEsitiQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>
  > = ({ signal }) =>
    getBackAttivitaIdEsiti(idTaskInstance, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdEsitiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>
>;
export type GetBackAttivitaIdEsitiQueryError = ErrorType<unknown>;

/**
 * @summary Esiti
 */
export const useGetBackAttivitaIdEsiti = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdEsiti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdEsitiQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Elenca gli endoprocedimenti avviabili
 * @summary Elenca endoprocedimenti avviabili
 */
export const getBackAttivitaIdEndoprocedimentiAvviabili = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseEndoprocedimentiAvviabili>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/endoprocedimentiAvviabili`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdEndoprocedimentiAvviabiliQueryKey = (
  idTaskInstance: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/endoprocedimentiAvviabili`,
  ] as const;

export const getGetBackAttivitaIdEndoprocedimentiAvviabiliQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdEndoprocedimentiAvviabiliQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>>
  > = ({ signal }) =>
    getBackAttivitaIdEndoprocedimentiAvviabili(
      idTaskInstance,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdEndoprocedimentiAvviabiliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>>
>;
export type GetBackAttivitaIdEndoprocedimentiAvviabiliQueryError =
  ErrorType<unknown>;

/**
 * @summary Elenca endoprocedimenti avviabili
 */
export const useGetBackAttivitaIdEndoprocedimentiAvviabili = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdEndoprocedimentiAvviabili>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackAttivitaIdEndoprocedimentiAvviabiliQueryOptions(
      idTaskInstance,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto = (
  idTaskInstance: string,
  idDocumento: string,
  idTipoSezione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/documento/${idDocumento}/sezioniDocumento/${idTipoSezione}/contenuto`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryKey =
  (idTaskInstance: string, idDocumento: string, idTipoSezione: string) =>
    [
      `/framework-bo-procedimenti/attivita/${idTaskInstance}/documento/${idDocumento}/sezioniDocumento/${idTipoSezione}/contenuto`,
    ] as const;

export const getGetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto>
    >,
    TError = ErrorType<unknown>,
  >(
    idTaskInstance: string,
    idDocumento: string,
    idTipoSezione: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto>
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryKey(
        idTaskInstance,
        idDocumento,
        idTipoSezione,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto
        >
      >
    > = ({ signal }) =>
      getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto(
        idTaskInstance,
        idDocumento,
        idTipoSezione,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(idTaskInstance && idDocumento && idTipoSezione),
      ...queryOptions,
    };
  };

export type GetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto>
    >
  >;
export type GetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryError =
  ErrorType<unknown>;

export const useGetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto>
  >,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idDocumento: string,
  idTipoSezione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getBackAttivitaIdDocumentoIdSezioniDocumentoIdContenuto
        >
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackAttivitaIdDocumentoIdSezioniDocumentoIdContenutoQueryOptions(
      idTaskInstance,
      idDocumento,
      idTipoSezione,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaIdDocumenti = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseCollectionAzioniDocumento>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/documenti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdDocumentiQueryKey = (idTaskInstance: string) =>
  [`/framework-bo-procedimenti/attivita/${idTaskInstance}/documenti`] as const;

export const getGetBackAttivitaIdDocumentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdDocumentiQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>
  > = ({ signal }) =>
    getBackAttivitaIdDocumenti(idTaskInstance, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdDocumentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>
>;
export type GetBackAttivitaIdDocumentiQueryError = ErrorType<unknown>;

export const useGetBackAttivitaIdDocumenti = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdDocumentiQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ricerca puntuale Allegato comunicazione
 * @summary Ricerca puntuale Allegato comunicazione
 */
export const getBackAttivitaIdComunicazioniAllegatiId = (
  idTaskInstance: string,
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/allegati/${idAllegato}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdComunicazioniAllegatiIdQueryKey = (
  idTaskInstance: string,
  idAllegato: string,
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/allegati/${idAllegato}`,
  ] as const;

export const getGetBackAttivitaIdComunicazioniAllegatiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdComunicazioniAllegatiIdQueryKey(
      idTaskInstance,
      idAllegato,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>
  > = ({ signal }) =>
    getBackAttivitaIdComunicazioniAllegatiId(
      idTaskInstance,
      idAllegato,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idTaskInstance && idAllegato),
    ...queryOptions,
  };
};

export type GetBackAttivitaIdComunicazioniAllegatiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>
>;
export type GetBackAttivitaIdComunicazioniAllegatiIdQueryError =
  ErrorType<unknown>;

/**
 * @summary Ricerca puntuale Allegato comunicazione
 */
export const useGetBackAttivitaIdComunicazioniAllegatiId = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdComunicazioniAllegatiIdQueryOptions(
    idTaskInstance,
    idAllegato,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un allegato di una comunicazione
 * @summary Cancella un allegato di una comunicazione
 */
export const deleteBackAttivitaIdComunicazioniAllegatiId = (
  idTaskInstance: string,
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/comunicazioni/allegati/${idAllegato}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackAttivitaIdComunicazioniAllegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniAllegatiId>>,
    TError,
    { idTaskInstance: string; idAllegato: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniAllegatiId>>,
  TError,
  { idTaskInstance: string; idAllegato: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniAllegatiId>>,
    { idTaskInstance: string; idAllegato: string }
  > = props => {
    const { idTaskInstance, idAllegato } = props ?? {};

    return deleteBackAttivitaIdComunicazioniAllegatiId(
      idTaskInstance,
      idAllegato,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackAttivitaIdComunicazioniAllegatiIdMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniAllegatiId>>
  >;

export type DeleteBackAttivitaIdComunicazioniAllegatiIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Cancella un allegato di una comunicazione
 */
export const useDeleteBackAttivitaIdComunicazioniAllegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdComunicazioniAllegatiId>>,
    TError,
    { idTaskInstance: string; idAllegato: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackAttivitaIdComunicazioniAllegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Elenco delle azioni su Allegati di procedimento previste in un dato task, incluse le informazioni relative agli eventuali file fisici eventualmente conferiti e non cancellati logicamente.
 * @summary Azioni su allegati
 */
export const getBackAttivitaIdAllegati = (
  idTaskInstance: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListAzioniGruppoAllegato>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdAllegatiQueryKey = (idTaskInstance: string) =>
  [`/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati`] as const;

export const getGetBackAttivitaIdAllegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdAllegatiQueryKey(idTaskInstance);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>
  > = ({ signal }) =>
    getBackAttivitaIdAllegati(idTaskInstance, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTaskInstance, ...queryOptions };
};

export type GetBackAttivitaIdAllegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>
>;
export type GetBackAttivitaIdAllegatiQueryError = ErrorType<unknown>;

/**
 * @summary Azioni su allegati
 */
export const useGetBackAttivitaIdAllegati = <
  TData = Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  idTaskInstance: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaIdAllegatiQueryOptions(
    idTaskInstance,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaXlsx = (
  params?: GetBackAttivitaXlsxParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/xlsx`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaXlsxQueryKey = (
  params?: GetBackAttivitaXlsxParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/xlsx`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaXlsx>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaXlsxParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackAttivitaXlsxQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaXlsx>>
  > = ({ signal }) => getBackAttivitaXlsx(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaXlsx>>
>;
export type GetBackAttivitaXlsxQueryError = ErrorType<unknown>;

export const useGetBackAttivitaXlsx = <
  TData = Awaited<ReturnType<typeof getBackAttivitaXlsx>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaXlsxParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaXlsxQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ritorna una lista con tutti i possibili stati di un'attività, di tipo StatoAttivitaDTO
 * @summary Stati attività
 */
export const getBackAttivitaStati = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListStatoAttivitaDTO>(
    { url: `/framework-bo-procedimenti/attivita/stati`, method: 'get', signal },
    options,
  );
};

export const getGetBackAttivitaStatiQueryKey = () =>
  [`/framework-bo-procedimenti/attivita/stati`] as const;

export const getGetBackAttivitaStatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaStati>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackAttivitaStati>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaStati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBackAttivitaStatiQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaStati>>
  > = ({ signal }) => getBackAttivitaStati(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaStatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaStati>>
>;
export type GetBackAttivitaStatiQueryError = ErrorType<unknown>;

/**
 * @summary Stati attività
 */
export const useGetBackAttivitaStati = <
  TData = Awaited<ReturnType<typeof getBackAttivitaStati>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackAttivitaStati>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaStatiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaCsv = (
  params?: GetBackAttivitaCsvParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/csv`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaCsvQueryKey = (
  params?: GetBackAttivitaCsvParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/csv`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaCsvQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaCsv>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaCsvParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaCsv>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackAttivitaCsvQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaCsv>>
  > = ({ signal }) => getBackAttivitaCsv(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaCsvQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaCsv>>
>;
export type GetBackAttivitaCsvQueryError = ErrorType<unknown>;

export const useGetBackAttivitaCsv = <
  TData = Awaited<ReturnType<typeof getBackAttivitaCsv>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackAttivitaCsvParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaCsvQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupero tutti i campi potenzialmente estraibili in csv per le attivita dei procedimenti (per verticale)
 */
export const getBackAttivitaCsvCampi = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CampoEstraibileCSV[]>(
    {
      url: `/framework-bo-procedimenti/attivita/csv/campi`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaCsvCampiQueryKey = () =>
  [`/framework-bo-procedimenti/attivita/csv/campi`] as const;

export const getGetBackAttivitaCsvCampiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackAttivitaCsvCampiQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>
  > = ({ signal }) => getBackAttivitaCsvCampi(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaCsvCampiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>
>;
export type GetBackAttivitaCsvCampiQueryError = ErrorType<unknown>;

export const useGetBackAttivitaCsvCampi = <
  TData = Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackAttivitaCsvCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaCsvCampiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getBackAttivitaAzioniFirmaDocumenti = (
  params: GetBackAttivitaAzioniFirmaDocumentiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListAzioniFirmaDocumentoResponse>(
    {
      url: `/framework-bo-procedimenti/attivita/azioniFirmaDocumenti`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaAzioniFirmaDocumentiQueryKey = (
  params: GetBackAttivitaAzioniFirmaDocumentiParams,
) =>
  [
    `/framework-bo-procedimenti/attivita/azioniFirmaDocumenti`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaAzioniFirmaDocumentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>,
  TError = ErrorType<unknown>,
>(
  params: GetBackAttivitaAzioniFirmaDocumentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaAzioniFirmaDocumentiQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>
  > = ({ signal }) =>
    getBackAttivitaAzioniFirmaDocumenti(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackAttivitaAzioniFirmaDocumentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>
>;
export type GetBackAttivitaAzioniFirmaDocumentiQueryError = ErrorType<unknown>;

export const useGetBackAttivitaAzioniFirmaDocumenti = <
  TData = Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>,
  TError = ErrorType<unknown>,
>(
  params: GetBackAttivitaAzioniFirmaDocumentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaAzioniFirmaDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackAttivitaAzioniFirmaDocumentiQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
